/**
 * Game Storage Utility
 * 
 * Handles persistent storage of game statistics and high scores
 * using localStorage with fallback support
 */

export interface GameStats {
    highScore: number // Legacy - now used for Endless mode high score
    endlessHighScore: number // Endless mode high score (explicit)
    timeAttackHighScore: number // Time Attack mode high score (separate leaderboard)
    totalTimePlayed: number // in seconds
    lastPlayed: number // timestamp
    gamesPlayed: number
    longestChain: number
    // New unlock tracking
    maxLevelReached: number // highest level reached in any mode
    endlessMaxLevel: number // highest level in Endless mode specifically
    timeAttackUnlocked: boolean
    puzzleUnlocked: boolean
    classicUnlocked: boolean
}

export class GameStorage {
    private static readonly STORAGE_KEY = 'sct-game-stats'
    private static readonly VERSION = '1.0'

    /**
     * Get all stored game statistics
     */
    public static getStats(): GameStats {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY)
            if (stored) {
                const data = JSON.parse(stored)
                // Validate data structure
                if (data.version === this.VERSION && data.stats) {
                    // Migrate old data to new format if needed
                    const stats = this.migrateStats(data.stats)
                    return stats
                }
            }
        } catch (error) {
            console.warn('Failed to load game stats from localStorage:', error)
        }

        // Return default stats if nothing stored or error occurred
        return this.getDefaultStats()
    }

    /**
     * Migrate old stats format to current format
     */
    private static migrateStats(oldStats: any): GameStats {
        const defaultStats = this.getDefaultStats()

        // Copy all existing fields
        const migratedStats: GameStats = { ...defaultStats, ...oldStats }

        // Handle missing mode-specific high scores
        if (typeof migratedStats.endlessHighScore === 'undefined') {
            // Use legacy highScore as endlessHighScore for backwards compatibility
            migratedStats.endlessHighScore = migratedStats.highScore || 0
        }

        if (typeof migratedStats.timeAttackHighScore === 'undefined') {
            migratedStats.timeAttackHighScore = 0
        }

        // Ensure all new unlock tracking fields exist
        if (typeof migratedStats.maxLevelReached === 'undefined') {
            migratedStats.maxLevelReached = 1
        }

        if (typeof migratedStats.endlessMaxLevel === 'undefined') {
            migratedStats.endlessMaxLevel = 1
        }

        if (typeof migratedStats.timeAttackUnlocked === 'undefined') {
            migratedStats.timeAttackUnlocked = false
        }

        if (typeof migratedStats.puzzleUnlocked === 'undefined') {
            migratedStats.puzzleUnlocked = false
        }

        if (typeof migratedStats.classicUnlocked === 'undefined') {
            migratedStats.classicUnlocked = false
        }

        // Save migrated stats back to storage
        this.saveStats(migratedStats)

        return migratedStats
    }

    /**
     * Save game statistics
     */
    public static saveStats(stats: GameStats): void {
        try {
            const data = {
                version: this.VERSION,
                stats: stats,
                lastSaved: Date.now()
            }
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data))
        } catch (error) {
            console.error('Failed to save game stats to localStorage:', error)
        }
    }

    /**
     * Update high score if current score is higher
     * Returns true if a new high score was set
     * @deprecated Use updateEndlessHighScore or updateTimeAttackHighScore instead
     */
    public static updateHighScore(score: number): boolean {
        const stats = this.getStats()
        if (score > stats.highScore) {
            stats.highScore = score
            // Also update endlessHighScore for backwards compatibility
            if (score > stats.endlessHighScore) {
                stats.endlessHighScore = score
            }
            this.saveStats(stats)
            return true
        }
        return false
    }

    /**
     * Update Endless mode high score
     * Returns true if a new high score was set
     */
    public static updateEndlessHighScore(score: number): boolean {
        const stats = this.getStats()
        if (score > stats.endlessHighScore) {
            stats.endlessHighScore = score
            // Also update legacy highScore for backwards compatibility
            if (score > stats.highScore) {
                stats.highScore = score
            }
            this.saveStats(stats)
            return true
        }
        return false
    }

    /**
     * Update Time Attack mode high score
     * Returns true if a new high score was set
     */
    public static updateTimeAttackHighScore(score: number): boolean {
        const stats = this.getStats()
        if (score > stats.timeAttackHighScore) {
            stats.timeAttackHighScore = score
            this.saveStats(stats)
            return true
        }
        return false
    }

    /**
     * Update longest chain if current chain is longer
     * Returns true if a new record was set
     */
    public static updateLongestChain(chain: number): boolean {
        const stats = this.getStats()
        if (chain > stats.longestChain) {
            stats.longestChain = chain
            this.saveStats(stats)
            return true
        }
        return false
    }

    /**
     * Add time to total time played (in seconds)
     */
    public static addTimePlayed(seconds: number): void {
        const stats = this.getStats()
        stats.totalTimePlayed += seconds
        stats.lastPlayed = Date.now()
        this.saveStats(stats)
    }

    /**
     * Increment games played counter
     */
    public static incrementGamesPlayed(): void {
        const stats = this.getStats()
        stats.gamesPlayed += 1
        this.saveStats(stats)
    }

    /**
     * Update stats after a game session
     * @param score - Final score from the game
     * @param timePlayed - Time played in seconds
     * @param longestChain - Longest chain achieved
     * @param mode - Game mode played ('endless' | 'timeAttack' | 'puzzle' | 'classic')
     */
    public static updateAfterGame(
        score: number,
        timePlayed: number,
        longestChain: number,
        mode: 'endless' | 'timeAttack' | 'puzzle' | 'classic' = 'endless'
    ): {
        newHighScore: boolean
        newLongestChain: boolean
    } {
        const stats = this.getStats()

        // Update mode-specific high score
        let newHighScore = false
        if (mode === 'endless') {
            newHighScore = score > stats.endlessHighScore
            if (newHighScore) {
                stats.endlessHighScore = score
                // Also update legacy highScore
                if (score > stats.highScore) {
                    stats.highScore = score
                }
            }
        } else if (mode === 'timeAttack') {
            newHighScore = score > stats.timeAttackHighScore
            if (newHighScore) {
                stats.timeAttackHighScore = score
            }
        } else {
            // For other modes, use legacy highScore
            newHighScore = score > stats.highScore
            if (newHighScore) stats.highScore = score
        }

        const newLongestChain = longestChain > stats.longestChain
        if (newLongestChain) stats.longestChain = longestChain

        stats.totalTimePlayed += timePlayed
        stats.gamesPlayed += 1
        stats.lastPlayed = Date.now()

        this.saveStats(stats)

        return { newHighScore, newLongestChain }
    }

    /**
     * Reset all statistics
     */
    public static resetStats(): void {
        this.saveStats(this.getDefaultStats())
    }

    /**
     * Get default statistics object
     */
    private static getDefaultStats(): GameStats {
        return {
            highScore: 0,
            endlessHighScore: 0,
            timeAttackHighScore: 0,
            totalTimePlayed: 0,
            lastPlayed: 0,
            gamesPlayed: 0,
            longestChain: 0,
            maxLevelReached: 1,
            endlessMaxLevel: 1,
            timeAttackUnlocked: false,
            puzzleUnlocked: false,
            classicUnlocked: false
        }
    }

    /**
     * Check if localStorage is available
     */
    public static isStorageAvailable(): boolean {
        try {
            const test = '__storage_test__'
            localStorage.setItem(test, test)
            localStorage.removeItem(test)
            return true
        } catch (error) {
            return false
        }
    }

    /**
     * Format time in seconds to human-readable string
     */
    public static formatTime(seconds: number): string {
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const secs = Math.floor(seconds % 60)

        if (hours > 0) {
            return `${hours}h ${minutes}m`
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`
        } else {
            return `${secs}s`
        }
    }

    /**
     * Update level progress and check for unlocks
     */
    public static updateLevelProgress(mode: string, level: number): void {
        const stats = this.getStats()

        // Update overall max level
        stats.maxLevelReached = Math.max(stats.maxLevelReached, level)

        // Update mode-specific max level
        if (mode === 'ENDLESS') {
            stats.endlessMaxLevel = Math.max(stats.endlessMaxLevel, level)
        }

        // Check and update unlock requirements
        stats.timeAttackUnlocked = stats.endlessMaxLevel >= 5
        stats.puzzleUnlocked = stats.endlessMaxLevel >= 15 // Complete Endless (reach max level)
        stats.classicUnlocked = stats.maxLevelReached >= 10 // Level 10 in any mode

        this.saveStats(stats)
    }

    /**
     * Check if a specific mode is unlocked
     */
    public static isModeUnlocked(mode: string): boolean {
        const stats = this.getStats()

        switch (mode) {
            case 'ENDLESS':
                return true // Always unlocked
            case 'TIME_ATTACK':
                return stats.timeAttackUnlocked
            case 'PUZZLE':
                return stats.puzzleUnlocked
            case 'CLASSIC':
                return stats.classicUnlocked
            default:
                return false
        }
    }

    /**
     * Get unlock progress for display
     */
    public static getUnlockProgress(): {
        endlessMaxLevel: number
        maxLevelReached: number
        timeAttackUnlocked: boolean
        puzzleUnlocked: boolean
        classicUnlocked: boolean
    } {
        const stats = this.getStats()
        return {
            endlessMaxLevel: stats.endlessMaxLevel,
            maxLevelReached: stats.maxLevelReached,
            timeAttackUnlocked: stats.timeAttackUnlocked,
            puzzleUnlocked: stats.puzzleUnlocked,
            classicUnlocked: stats.classicUnlocked
        }
    }
}
